import { useEffect, useState } from "react";
import { Check, Coins, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [topups, setTopups] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans);
      setTopups(r.data.topups);
    });
  }, []);

  const checkout = async (kind, id) => {
    setLoadingId(id);
    try {
      const body = kind === "plan" ? { plan_id: id, origin_url: window.location.origin } : { topup_id: id, origin_url: window.location.origin };
      const { data } = await api.post("/billing/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoadingId(null);
    }
  };

  const planOrder = ["pro", "business"];
  const topupOrder = ["topup_50", "topup_200", "topup_1000"];

  const isFreeUser = !user?.subscription_status || user?.subscription_status !== "active";

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto">
      <div className="mb-8 sm:mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-2">Plans & Billing</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Choose your plan.</h1>
        <p className="text-[#64748B] mt-2 text-sm sm:text-base">Current: <span className="font-semibold text-[#1F3A8A]">{(user?.plan || "free").toUpperCase()}</span> · {user?.tokens_remaining} tokens left</p>
      </div>

      {/* Plans grid: Free + 2 paid */}
      <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
        {/* Free tier card */}
        <div data-testid="plan-card-free" className="relative p-6 sm:p-8 bg-white border border-[#E2E8F0] rounded-md">
          <h3 className="font-display text-2xl font-bold text-[#1F3A8A]">Free</h3>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="font-display text-4xl sm:text-5xl font-extrabold">Free</span>
          </div>
          <div className="text-sm text-[#64748B] mt-1">50 tokens / month</div>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> 50 prompts per month</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> Full prompt history & filters</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> All AI tool presets</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#F59E0B] mt-0.5" /> Includes light in-app ads</li>
          </ul>
          <Button disabled className="w-full mt-8 bg-[#F5F7FA] text-[#64748B] cursor-default" data-testid="free-current-btn">
            {isFreeUser ? "Current plan" : "Free tier"}
          </Button>
        </div>

        {planOrder.map((id) => {
          const p = plans[id];
          if (!p) return null;
          const highlight = id === "pro";
          const current = user?.plan === id && user?.subscription_status === "active";
          return (
            <div
              key={id}
              data-testid={`plan-card-${id}`}
              className={`relative p-6 sm:p-8 bg-white border rounded-md ${highlight ? "border-[#14B8A6] shadow-lg" : "border-[#E2E8F0]"}`}
            >
              {highlight && (
                <div className="absolute -top-3 left-6 sm:left-8 px-3 py-1 bg-[#14B8A6] text-white text-xs font-bold uppercase tracking-wider rounded">Most popular</div>
              )}
              <h3 className="font-display text-2xl font-bold text-[#1F3A8A]">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl sm:text-5xl font-extrabold">${p.price}</span>
                <span className="text-[#64748B]">/mo</span>
              </div>
              <div className="text-sm text-[#64748B] mt-1">{p.tokens} tokens / month</div>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> {p.tokens} prompts per cycle</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> Ad-free experience</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> Full prompt history & filters</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#14B8A6] mt-0.5" /> Re-generate variations</li>
              </ul>
              <Button
                onClick={() => checkout("plan", id)}
                disabled={loadingId === id || current}
                data-testid={`subscribe-${id}`}
                className={`w-full mt-8 ${highlight ? "bg-[#14B8A6] hover:bg-[#0D9488] text-white" : "bg-white border border-[#1F3A8A] text-[#1F3A8A] hover:bg-[#1F3A8A] hover:text-white"}`}
              >
                {current ? "Current plan" : loadingId === id ? "Redirecting…" : `Subscribe to ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Top-ups */}
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-2">One-time</div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Token top-ups</h2>
        <p className="text-[#64748B] mt-1">Top up anytime — never lose momentum.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {topupOrder.map((id) => {
          const t = topups[id];
          if (!t) return null;
          return (
            <div key={id} data-testid={`topup-card-${id}`} className="p-6 bg-white border border-[#E2E8F0] rounded-md card-hover flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Coins className="w-4 h-4 text-[#14B8A6]" />
                  <span className="font-display text-xl font-bold">{t.name}</span>
                </div>
                <div className="font-display text-3xl font-extrabold text-[#1F3A8A]">${t.price}</div>
              </div>
              <Button
                onClick={() => checkout("topup", id)}
                disabled={loadingId === id}
                className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white"
                data-testid={`buy-${id}`}
              >
                {loadingId === id ? "…" : "Buy"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
